import React, { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FaSave, FaTimes, FaUser, FaPhoneAlt, FaMapMarkerAlt, FaIdCard, FaAddressCard } from "react-icons/fa";
import { toast } from "react-toastify";
import { getAuthToken } from "../../../../util/auth";
import { getApiChara } from "../../../../util/getApi";
import ConfirmModal from "../../../UI/ConfirmModal";
import { useTranslation } from "react-i18next";
import styles from "./AddPatientModal.module.css";

const AddPatientModal = ({ show, onHide, onPatientAdded }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    nickName: "",
    dob: "",
    maritalStatus: "single",
    religion: "",
    caste: "",
    panCardNo: "",
    adharCardNo: "",
    gender: "male",
    age: "",
    occupation: "",
    identificationMark: "",
    emailId: "",
    mobile: "",
    alternateMobileNum: "",
    address: "",
    city: "",
    pincode: "",
    referredBy: "",
    registrationNo: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "dob" && value) {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        updated.age = age >= 0 ? age : 0;
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      toast.error("Session expired, please login again");
      setIsSubmitting(false);
      return;
    }

    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/patient/addPatient`;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        body: JSON.stringify(formData),
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      const result = await response.json();

      if (result?.errorMsg) {
        toast.error("Patient Already Exists");
      } else if (!response.ok) {
        toast.error("Could not save Patient");
      } else {
        toast.success("Patient added successfully!");
        // ✅ Pass data back to parent
        if (onPatientAdded) {
          onPatientAdded(result); // assuming backend returns patient object
        }
        onHide();
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving patient");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="patientModel">
      <Modal.Header closeButton style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}>
        <Modal.Title className="w-100 text-center text-white" style={{ color: 'white' }}>Add New Patient</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Section: Personal Information */}
          <div className={styles.sectionHeader}>
            <FaIdCard /> {t("Personal Information")}
          </div>
          <div className="row g-3">
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("First Name")} <span className={styles.reqSign}>*</span></label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Middle Name")}</label>
              <input type="text" name="middleName" value={formData.middleName} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Last Name")} <span className={styles.reqSign}>*</span></label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Gender")} <span className={styles.reqSign}>*</span></label>
              <select name="gender" value={formData.gender} onChange={handleChange} required className="form-select form-select-sm">
                <option value="male">{t("Male")}</option>
                <option value="female">{t("Female")}</option>
                <option value="other">{t("Other")}</option>
              </select>
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Date of Birth")}</label>
              <input type="date" name="dob" value={formData.dob} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Age")}</label>
              <input type="number" name="age" value={formData.age} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Nick Name")}</label>
              <input type="text" name="nickName" value={formData.nickName} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Marital Status")}</label>
              <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} className="form-select form-select-sm">
                <option value="single">{t("Single")}</option>
                <option value="married">{t("Married")}</option>
                <option value="divorced">{t("Divorced")}</option>
                <option value="widowed">{t("Widowed")}</option>
              </select>
            </div>
          </div>

          {/* Section: Identity & Background */}
          <div className={styles.sectionHeader}>
            <FaAddressCard /> {t("Identity & Background")}
          </div>
          <div className="row g-3">
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Religion")}</label>
              <input type="text" name="religion" value={formData.religion} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Caste")}</label>
              <input type="text" name="caste" value={formData.caste} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Occupation")}</label>
              <input type="text" name="occupation" value={formData.occupation} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Identification Mark")}</label>
              <input type="text" name="identificationMark" value={formData.identificationMark} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("PAN Card No")}</label>
              <input type="text" name="panCardNo" value={formData.panCardNo} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Aadhar Card No")}</label>
              <input type="text" name="adharCardNo" value={formData.adharCardNo} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Registration No")}</label>
              <input type="text" name="registrationNo" value={formData.registrationNo} onChange={handleChange} className="form-control form-control-sm" />
            </div>
          </div>

          {/* Section: Contact Information */}
          <div className={styles.sectionHeader}>
            <FaPhoneAlt /> {t("Contact Information")}
          </div>
          <div className="row g-3">
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Mobile")}</label>
              <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-3 col-6">
              <label className={styles.label}>{t("Alt Mobile No")}</label>
              <input type="tel" name="alternateMobileNum" value={formData.alternateMobileNum} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-6 col-12">
              <label className={styles.label}>{t("Email")}</label>
              <input type="email" name="emailId" value={formData.emailId} onChange={handleChange} className="form-control form-control-sm" />
            </div>
          </div>

          {/* Section: Address & Reference */}
          <div className={styles.sectionHeader}>
            <FaMapMarkerAlt /> {t("Address & Reference")}
          </div>
          <div className="row g-3">
            <div className="col-md-12">
              <label className={styles.label}>{t("Address")} <span className={styles.reqSign}>*</span></label>
              <input type="text" name="address" value={formData.address} onChange={handleChange} required className="form-control form-control-sm" />
            </div>
            <div className="col-md-4 col-4">
              <label className={styles.label}>{t("City")}</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-4 col-4">
              <label className={styles.label}>{t("Pincode")}</label>
              <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} className="form-control form-control-sm" />
            </div>
            <div className="col-md-4 col-4">
              <label className={styles.label}>{t("Referred By")}</label>
              <input type="text" name="referredBy" value={formData.referredBy} className="form-control form-control-sm" onChange={(e) => {
                const { name, value } = e.target;
                const filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
                setFormData((prev) => ({ ...prev, [name]: filteredValue }));
              }} />
            </div>
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            <Button
              type="submit"
              disabled={isSubmitting}
              className={styles.themeBtn}
            >
              {isSubmitting ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
              {isSubmitting ? t("Saving...") : t("SAVE")}
            </Button>
            <Button
              type="button"
              className={styles.themeBtn}
              onClick={onHide}
            >
              <FaTimes className="me-2" /> {t("CANCEL")}
            </Button>
          </div>
        </form>
      </Modal.Body>
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={confirmSubmit}
        title={t("Register Patient")}
        message={t("Are you sure you want to register this patient?")}
        variant="primary"
        confirmText={t("Confirm Registration")}
      />
    </Modal>
  );
};

export default AddPatientModal;
